/**
 * ============================================================================
 * RcaTimeline — Phase 7 frontend (Deviation Management Module).
 * Owner: Phase 7 frontend agent.
 * Purpose: Render the deviation_history chain as a vertical timeline.
 *          Each event_code is mapped to a colour (FSM advance = primary,
 *          cascade = success, dual-sign = primary, breach = danger).
 *
 * URS rows: DEV-001 (FSM trail), DEV-008 (RCA traceability).
 * ============================================================================ */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './DeviationDetail.module.css';
import type { HistoryEntry } from './useDeviationDetailQuery';

const DANGER_CODES = new Set<string>([
  'DEVIATION_SLA_BREACHED',
  'DEVIATION_ESCALATED',
  'DEVIATION_CANCELLED',
  'CRITICAL_DUAL_SIGN_REQUIRED',
]);

const SUCCESS_CODES = new Set<string>([
  'DEVIATION_CLOSED',
  'DEVIATION_RCA_COMPLETED',
  'CRITICAL_DUAL_SIGN_COMPLETED',
  'DEVIATION_CAPA_OPENED',
]);

const MUTED_CODES = new Set<string>([
  'DEVIATION_NOTE_APPENDED',
  'DEVIATION_EVIDENCE_LINKED',
]);

function dotClassForCode(code: string): string {
  if (DANGER_CODES.has(code)) return styles.timelineDotDanger;
  if (SUCCESS_CODES.has(code)) return styles.timelineDotSuccess;
  if (MUTED_CODES.has(code)) return styles.timelineDotMuted;
  return styles.timelineDot; // default primary
}

function summarisePayload(payload: Record<string, unknown>): string {
  if (!payload || Object.keys(payload).length === 0) return '';
  const interesting = [
    'classification',
    'confirmed_classification',
    'previous_status',
    'sla_due_utc',
    'equipment_id',
    'capa_id',
    'note_kind',
    'reason',
  ];
  const parts: string[] = [];
  for (const k of interesting) {
    const v = payload[k];
    if (v == null) continue;
    parts.push(`${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`);
    if (parts.length >= 4) break;
  }
  return parts.join(', ');
}

export interface RcaTimelineProps {
  history: HistoryEntry[];
}

export function RcaTimeline({ history }: RcaTimelineProps) {
  const { t, i18n } = useTranslation();
  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
    [i18n.language],
  );

  if (!history || history.length === 0) {
    return (
      <div className={styles.emptyNote}>
        {t('deviation.timeline.empty', {
          defaultValue: 'No history events yet.',
        })}
      </div>
    );
  }

  return (
    <ol className={styles.timeline} aria-label={t('deviation.timeline.aria', { defaultValue: 'Deviation history timeline' })}>
      {history.map((e) => (
        <li key={e.eventId} className={styles.timelineItem}>
          <span className={dotClassForCode(e.eventCode)} aria-hidden="true" />
          <div className={styles.timelineBody}>
            <span className={styles.timelineCode}>{e.eventCode}</span>
            <span className={styles.timelineMeta}>
              {dateFmt.format(new Date(e.eventAtUtc))}
              {e.actorUserId ? ` · ${e.actorUserId}` : ` · ${t('deviation.timeline.system', { defaultValue: 'system' })}`}
            </span>
            {summarisePayload(e.payload) && (
              <span className={styles.timelinePayload}>
                {summarisePayload(e.payload)}
              </span>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}
